import { FC, useContext, useState, useRef, MutableRefObject } from 'react';
import { boolean, date, mixed, object, string } from 'yup';
import { Formik, Form } from 'formik';
import { Container, Row, Col } from 'react-bootstrap';
import DateTimePicker from 'react-datetime-picker';

import styles from './ProxieSuggestionForm.module.scss';

import { ModalComponent as Modal } from '../Modal/Modal';
import AppContext from '../../../store/appContext';

// input components
import BStrapFormikTextArea from '../BStrapFormikTextArea';
import BStrapFormikCheckbox from '../BStrapFormikCheckbox';
import BStrapFormikInput from '../BStrapFormikInput';
import StyledErrorMessage from '../StyledErrorMessage/StyledErrorMessage';

import '../../../styles/custom.scss';
import '../../../styles/DateTimePicker.css';

//form custom helper functions
import {
  checkIfFilesAreTooBig,
  checkIfFilesAreCorrectType,
  checkIfFilesAreOverLimit3,
  checkIfNoFiles,
  toArray,
} from '../SuggestionForm/helperFunctions';
import BStrapFormikSelect from '../BStrapFormikSelect';

interface Values {
  description: string;
  email: string;
  denyContact: boolean;
  gdpr_agreement: boolean;
  addPhoto: boolean;
  files?: File | null;
  dateTime: Date;
  objectType: string | null;
  objectId: string;
  category: string | null;
}

export interface IProxioSuggestionForm {
  subcategory_id: number;
  email: string;
  description: string;
  source: "SP";
  date: string;
  gdpr_agreement: boolean;
}

const style = {
  border: '3px solid #2dcfa7',
};

// Yup validation schema
const SuggestionSchema = object().shape({
  description: string()
    .min(5, 'Příliš krátké! Min 5 znaků')
    .max(500, 'Příliš dlouhé! (max 500 znaků)'),
  email: string().when('denyContact', {
    is: false,
    then: string().email('Neplatná e-mailová adresa').required('Vyplňte, prosím').nullable(),
  }),
  dateTime: date().required('Vyplňte, prosím'),
  objectType: string().nullable(),
  category: string().when('objectType', {
    is: (val: string | null) => !!val,
    then: string().required('Vyberte typ poruchy'),
    otherwise: string().nullable(),
  }),
  addPhoto: boolean(),
  files: mixed().when('addPhoto', {
    is: true,
    then: mixed()
      // .nullable()
      .required('Přiložte soubor/y')
      .test(
        'fileSize',
        'Soubor je příliš veliký, max velikost je 5 MB/soubor',
        checkIfFilesAreTooBig
      )
      .test(
        'fileType',
        'Neplatný typ souboru (povolené jsou jpg, gif a png)',
        checkIfFilesAreCorrectType
      )
      .test('fileCount', 'Maximální počet souborů je 3', checkIfFilesAreOverLimit3)
      .test('fileCount', 'Přiložte soubor/y', checkIfNoFiles),
  }),
  denyContact: boolean(),
  gdpr_agreement: boolean().when('denyContact', {
    is: false,
    then: boolean()
      .required('Bez přijetí podmínek nemůže být formulář odeslán.')
      .oneOf([true], 'Bez přijetí podmínek nemůže být formulář odeslán.'),
  }),
});

interface ISearchParams {
    inventaryId: string | null,
    category: string | null,
    value: string | null,
    utm_source: string | null,
    utm_medium: string | null,
    utm_campaign: string | null,
}

// the form
interface FProps {
  id: number;
  searchParams: ISearchParams
}

const CATEGORY_OPTIONS: Record<'mm' | 'vo', { value: string; label: string, contactRequired: boolean }[]> = {
  mm: [
    { value: 'graffiti-a-cerna-reklama', label: 'Graffiti a černá reklama', contactRequired: false },
    { value: 'rozbite-sklo', label: 'Rozbité sklo', contactRequired: false },
    { value: 'poskozena-tv', label: 'Poškozená TV', contactRequired: false },
    { value: 'poskozeny-reklamni-panel', label: 'Poškozený reklamní panel', contactRequired: false },
    { value: 'neporadek', label: 'Nepořádek', contactRequired: false },
    { value: 'vypadla-dvirka', label: 'Vypadlá dvířka', contactRequired: false },
    { value: 'ostatni', label: 'Ostatní', contactRequired: false },
  ],
  vo: [
    { value: 'nesviti', label: 'Nesvítí', contactRequired: false },
    { value: 'poskozeni', label: 'Poškození', contactRequired: false },
    { value: 'vypadla-dvirka', label: 'Vypadlá dvířka', contactRequired: false },
    { value: 'cerna-reklama', label: 'Černá reklama', contactRequired: false },
    { value: 'rusive-svetlo', label: 'Rušivé světlo', contactRequired: true },
    { value: 'ostatni', label: 'Ostatní', contactRequired: false },
  ],
};

const ProxioSuggestionForm: FC<FProps> = (props) => {
    const { error, regNum, sendAll, setShow, sendSuggestion } = useContext(AppContext);

    const [valueDateTime, setDateTime] = useState(new Date());
    const fileRef = useRef() as MutableRefObject<HTMLInputElement>;

    const allowedTypes = ['vo', 'mm'] as const;
        const isValidType = allowedTypes.includes(
        props.searchParams.category as (typeof allowedTypes)[number]
    );

    const initialType = isValidType ? props.searchParams.category! : '';
    const initialId = props.searchParams.inventaryId ?? '';

    const categoryList = isValidType
      ? CATEGORY_OPTIONS[initialType as 'mm' | 'vo']
      : [];

    const isValidCategoryValue = categoryList.some(
      (option) => option.value === props.searchParams.value
    );

    const initialCategory = isValidCategoryValue
      ? props.searchParams.value!
      : '';

    const initialValues: Values = {
        description: '',
        email: '',
        gdpr_agreement: false,
        denyContact: false,
        addPhoto: false,
        files: null,
        dateTime: valueDateTime,
        objectId: initialId,
        objectType: initialType,
        category: initialCategory
    };

    const isContactRequired = (objectType: string | null, category: string | null): boolean => {
        if (!objectType || !category) return false;
        const categoryOptions = CATEGORY_OPTIONS[objectType as 'mm' | 'vo'];
        const selectedCategory = categoryOptions?.find(option => option.value === category);
        return selectedCategory?.contactRequired || false;
    };

    const modalTitle = !error ? 'Děkujeme za Váš podnět' : 'Chyba';
    const modalBodyText = !error
        ? `Vašemu podnětu s registračním číslem ${regNum} se budeme náležitě věnovat.`
        : error;

  return (
    <Container className="mb-5">
      <Modal title={modalTitle} body={modalBodyText} />
      <Formik
        initialValues={initialValues}
        validationSchema={SuggestionSchema}
        validateOnBlur={true}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          // reset email and gdpr if sender wants to stay anonymous
          if (values.denyContact) {
            if (isContactRequired(values.objectType, values.category)) {
              actions.setSubmitting(false);
              return;
            }

            values.email = 'anonym@zmente.to';
            values.gdpr_agreement = false;
          }
          // remove photos if attachments unchecked
          if (!values.addPhoto) {
            values.files = null;
            values.addPhoto = false;
          }
          values.dateTime = valueDateTime;

          const fullDescription = `ID objektu: ${values.objectId || '-'}; Kategorie: ${values.category || '-'}; Popis: ${values.description || '-'}`;

          const sugForm: IProxioSuggestionForm = {
            subcategory_id: props.id,
            email: values.email,
            description: fullDescription,
            date: values.dateTime.toISOString(),
            gdpr_agreement: values.gdpr_agreement,
            source: "SP"
          };
          if (values.files) {
            const fileListArr = toArray(values.files as unknown as FileList);
            sendAll(fileListArr, sugForm);
            setShow(() => true);
          } else {
            sendSuggestion(sugForm);
            setShow(() => true);
          }
          actions.setSubmitting(false);
          actions.resetForm();
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => {
          const contactRequired = isContactRequired(values.objectType, values.category);

          if (contactRequired && values.denyContact) setFieldValue('denyContact', false);
          
          return (
            <Form className="mx-3 text-dark">
              <Row>
                <Col lg={12}>
                  <div className="mb-3">
                      <BStrapFormikInput
                          id="objectId"
                          name="objectId"
                          label="Číslo mobiliáře"
                          placeholder="Číslo mobiliáře"
                          type="textarea"
                          style={style}
                      />
                  </div>
                  <div className="mb-3">
                      <BStrapFormikSelect
                          id="objectType"
                          name="objectType"
                          label="Typ mobiliáře"
                          placeholder="Vyberte typ mobiliáře"
                          options={[
                              { value: 'mm', label: 'Zastávka MHD' },
                              { value: 'vo', label: 'Veřejné osvětlení' },
                          ]}
                          style={style}
                      />
                  </div>
                  <div className="mb-3">
                      <BStrapFormikSelect
                          id="category"
                          name="category"
                          label="Kategorie"
                          placeholder="Vyberte typ poruchy"
                          options={
                              values.objectType && CATEGORY_OPTIONS[values.objectType as 'mm' | 'vo']
                                  ? CATEGORY_OPTIONS[values.objectType as 'mm' | 'vo']
                                  : []
                          }
                          disabled={!values.objectType}
                          style={style}
                      />
                  </div>
                  <div className="mb-3">
                    <BStrapFormikTextArea
                      id="description"
                      name="description"
                      label="Popis (nepovinné)"
                      placeholder="Popis situace"
                      type="textarea"
                      style={style}
                    />
                  </div>
                  <label className="form-label d-block" htmlFor="datetime">
                    Datum a čas nálezu
                  </label>
                  <DateTimePicker
                    className="mb-3"
                    value={valueDateTime}
                    onChange={setDateTime}
                    maxDate={new Date(Date.now())}
                    locale={'cs-CZ'}
                    clearIcon={null}
                  />
                  {errors.dateTime && touched.dateTime ? (
                    <StyledErrorMessage>{errors.dateTime}</StyledErrorMessage>
                  ) : null}
                  <div className="mb-3">
                    <BStrapFormikCheckbox
                      name="addPhoto"
                      id="addPhoto"
                      label="připojit fotografie"
                      type="checkbox"
                      style={style}
                    />
                  </div>
                  {values.addPhoto && (
                    <div className="mb-3">
                      <input
                        ref={fileRef}
                        className="form-control mb-3"
                        style={style}
                        name="files"
                        id="files"
                        type="file"
                        accept="image/jpg, image/jpeg, image/gif, image/png"
                        multiple
                        onChange={(event) => {
                          setFieldValue('files', event.currentTarget.files);
                        }}
                      />
                      <button
                        className="btn btn-outline-dark"
                        type="button"
                        style={style}
                        onClick={() => (fileRef.current.value = '')}
                      >
                        Odstranit soubory
                      </button>
                      {errors.files && touched.files ? (
                        <StyledErrorMessage>{errors.files}</StyledErrorMessage>
                      ) : null}
                    </div>
                  )}
                  {!values.denyContact && (
                    <div>
                      <div className="mb-3">
                        <BStrapFormikInput
                          id="email"
                          name="email"
                          label="Váš e-mail"
                          placeholder="Váš e-mail"
                          type="email"
                          style={style}
                        />
                      </div>
                      <div className="mb-3">
                        <BStrapFormikCheckbox
                          name="gdpr_agreement"
                          id="gdpr_agreement"
                          label="souhlasím se zpracováním osobních údajů"
                          type="checkbox"
                          style={style}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-3">
                      <BStrapFormikCheckbox
                          name="denyContact"
                          id="denyContact"
                          label="nechci vyplnit e-mail a být kontaktován"
                          type="checkbox"
                          style={style}
                          disabled={contactRequired}
                      />
                      { contactRequired && (
                          <small className="text-muted d-block mt-1">
                              Pro vybranou kategorii je vyžadován kontakt.
                          </small>
                      )}
                  </div>
                  <button
                    className={`btn ${styles['send-button']}`}
                    type="submit"
                    style={style}
                    disabled={isSubmitting}
                  >
                    Odeslat
                  </button>
                </Col>
              </Row>
            </Form>
          )
        }}
      </Formik>
    </Container>
  );
};

export default ProxioSuggestionForm;